import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { currentUser, hasAdminAccess, loginWithPassword, setToken, MIN_LEVEL, kakaoLoginUrl } from '@/lib/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser() && hasAdminAccess()) navigate('/admin', { replace: true });
  }, [navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await loginWithPassword(email.trim(), password);
      if (!res.success || !res.token || !res.user) {
        setError(res.message ?? '로그인 실패');
        return;
      }
      if (res.user.level < MIN_LEVEL) {
        setError(`권한 부족 (필요 Lv.${MIN_LEVEL}, 현재 Lv.${res.user.level})`);
        return;
      }
      setToken(res.token);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : '네트워크 오류');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/20 px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg border bg-card p-8 shadow-sm"
        noValidate
      >
        <div className="mb-6 text-center">
          <h1 className="text-xl font-bold">Site Manager</h1>
          <p className="mt-1 text-xs text-muted-foreground">원데이샘플 SSO 로그인 (Lv.{MIN_LEVEL}+ 필요)</p>
        </div>

        <a
          href={kakaoLoginUrl()}
          className="mb-4 flex w-full items-center justify-center gap-2 rounded-md py-2.5 text-sm font-bold"
          style={{ background: '#FEE500', color: '#3C1E1E' }}
        >
          💬 카카오로 로그인
        </a>
        <div className="mb-4 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="h-px flex-1 bg-border" />또는 이메일로<span className="h-px flex-1 bg-border" />
        </div>

        <label className="mb-1 block text-xs font-medium text-muted-foreground" htmlFor="email">
          이메일
        </label>
        <input
          id="email"
          type="email"
          required
          autoFocus
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        />

        <label className="mb-1 block text-xs font-medium text-muted-foreground" htmlFor="password">
          비밀번호
        </label>
        <input
          id="password"
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-4 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        />

        {error && (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? '로그인 중…' : '로그인'}
        </button>

        <p className="mt-4 text-center text-[13px]">
          <a href="https://ai.onedaysample.com/login?mode=register&redirect=https://site-manager.onedaysample.com/" target="_blank" rel="noreferrer" className="text-primary underline">회원가입 / 비밀번호 찾기</a>
        </p>
      </form>
    </div>
  );
}
